import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import adminApiService from '../services/api';
import { UserDetail } from '../types';

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;

    const fetchUserDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await adminApiService.getUserDetail(userId);
        setUser(response.user);
        setLoading(false);
      } catch (err: any) {
        setError(err.message || 'Failed to load user details');
        setLoading(false);
      }
    };

    fetchUserDetail();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading user details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">Error: {error}</div>
        <Link to="/users" className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline">
          Back to Users
        </Link>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-600">User not found</div>
        <Link to="/users" className="mt-4 text-sm text-blue-600 hover:text-blue-800 underline">
          Back to Users
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/users" className="text-sm text-blue-600 hover:text-blue-800">
            ← Back to Users
          </Link>
          <h1 className="mt-2 text-2xl font-bold text-gray-900">
            User Details: {user.email || 'No email'}
          </h1>
        </div>
      </div>

      {/* Subscription Info */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Subscription</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Plan</label>
            <p className="mt-1 text-lg">
              <span className="px-2 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                {user.subscription.plan}
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Status</label>
            <p className="mt-1 text-lg">
              <span
                className={`px-2 py-1 text-sm font-semibold rounded-full ${
                  user.subscription.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : user.subscription.status === 'canceled'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {user.subscription.status}
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Period Start</label>
            <p className="mt-1 text-sm text-gray-900">
              {user.subscription.startDate
                ? new Date(user.subscription.startDate).toLocaleString()
                : 'N/A'}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Period End</label>
            <p className="mt-1 text-sm text-gray-900">
              {user.subscription.endDate
                ? new Date(user.subscription.endDate).toLocaleString()
                : 'N/A'}
            </p>
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-500">PayPal payer ID</label>
            <p className="mt-1 text-xs font-mono text-gray-700 break-all">
              {user.subscription.paypalPayerId || '—'}
            </p>
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-500">PayPal subscription ID</label>
            <p className="mt-1 text-xs font-mono text-gray-700 break-all">
              {user.subscription.paypalSubscriptionId || '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Quota Section */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Current Period Usage</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuotaItem label="Input Tokens" value={user.quota.inputTokens.toLocaleString()} />
          <QuotaItem label="Output Tokens" value={user.quota.outputTokens.toLocaleString()} />
          <QuotaItem
            label="Cache Read Tokens"
            value={user.quota.cacheReadTokens.toLocaleString()}
          />
          <QuotaItem
            label="Cache Write Tokens"
            value={user.quota.cacheWriteTokens.toLocaleString()}
          />
          <QuotaItem
            label="Transcription (min)"
            value={user.quota.transcriptionMinutes.toLocaleString()}
          />
          <QuotaItem
            label="Rekognition (min)"
            value={user.quota.rekognitionMinutes.toLocaleString()}
          />
          <QuotaItem
            label="SageMaker (sec)"
            value={user.quota.sagemakerSeconds.toLocaleString()}
          />
          <QuotaItem
            label="Hugging Face Jobs"
            value={user.quota.huggingFaceJobs.toLocaleString()}
          />
        </div>
      </div>

      {/* Chats List */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Chats ({user.chats.length})</h2>
        {user.chats.length === 0 ? (
          <p className="text-gray-500">No chats found</p>
        ) : (
          <div className="space-y-2">
            {user.chats.map((chat) => (
              <Link
                key={chat.chatId}
                to={`/chats/${chat.chatId}`}
                className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{chat.title}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {chat.messageCount} messages • Created{' '}
                      {new Date(chat.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    Updated {new Date(chat.lastUpdated).toLocaleString()}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuotaItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-500">{label}</label>
      <p className="mt-1 text-lg font-semibold text-gray-900">{value}</p>
    </div>
  );
}

